import { AppForm } from '../../../../../../common/forms/app.form';
import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite, LocationDecisionAppEntityLocationDecisionWrite,
  ParcelAppEntityParcelWrite
} from '../../../../../../openapi';
import { CustomValidator, fromToDate } from '../../../../../../common/forms/custom.validators';
import { toNumber } from '../../../../../../services/helpers';
import { RoadControlsForm } from '../../components/road-controls-set/road-controls.form';
import { environment } from '../../../../../../../environments/environment';

export type TaskNameType =
  'Zabudowy jednorodzinnej'
  | 'Zabudowy wielorodzinnej'
  | 'Obiektu handlowego'
  | 'Obiektu przemysłowego'
  | 'Inne'
  | string;

@Injectable()
export class LocalisationForm extends RoadControlsForm {

  public taskNameList: TaskNameType[] = [
    'Zabudowy jednorodzinnej',
    'Zabudowy wielorodzinnej',
    'Obiektu handlowego',
    'Obiektu przemysłowego',
    'Inne'
  ];

  constructor() {
    super({
      geometry: new FormControl(null, environment.production ? Validators.required : []),
      parcels: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.minLength(1), Validators.maxLength(255)]
        }
      )
    }, {
      validators: [fromToDate('startDate', 'endDate')]
    });
  }

  public calcArea(): void {
    let width = this.width?.value;
    let length = this.length?.value;
    if (!width || !length) {
      return;
    }
    width = (width?.toString() || '0').replaceAll(',', '.').replaceAll(' ', '');
    length = (length?.toString() || '0').replaceAll(',', '.').replaceAll(' ', '');
    this.area?.setValue((Number(width) * Number(length)).toFixed(2).replaceAll('.', ','));
  }

  public createAreaControls(): void {
    this.addControl('length', new FormControl(null, {
      validators: [Validators.required, Validators.maxLength(255)]
    }));
    this.addControl('width', new FormControl(null, {
      validators: [Validators.required, Validators.maxLength(255)]
    }));
    this.addControl('area', new FormControl({ disabled: true, value: null }, {
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get length(): FormControl | null {
    return this.get('length') as FormControl | null;
  }

  public get width(): FormControl | null {
    return this.get('width') as FormControl | null;
  }

  public get area(): FormControl | null {
    return this.get('area') as FormControl | null;
  }

  public get leasePurpose(): FormControl {
    return this.get('leasePurpose') as FormControl;
  }

  public createLeasePurposeControl(): void {
    this.addControl('leasePurpose', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
  }

  public get constructionTechnologyDescription(): FormControl | null {
    return this.get('constructionTechnologyDescription') as FormControl | null;
  }

  public createConstructionTechnologyDescription(): void {
    this.addControl('constructionTechnologyDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
  }

  public get showDateControls(): boolean {
    return !!this.startDate && !!this.endDate;
  }

  public get startDate(): FormControl | null {
    return this.get('startDate') as FormControl | null;
  }

  public get endDate(): FormControl | null {
    return this.get('endDate') as FormControl | null;
  }

  public createDateControls(): void {
    this.addControl('startDate', new FormControl(null, [CustomValidator.isValidDateFormat(), Validators.required, CustomValidator.minDate(new Date())]));
    this.addControl('endDate', new FormControl(null, [CustomValidator.isValidDateFormat(), Validators.required]));
  }

  public get locationDecisions(): FormControl | null {
    return this.get('locationDecisions') as FormControl | null;
  }

  public get locationDecisionsValues(): LocationDecisionAppEntityLocationDecisionWrite[] {
    return this.locationDecisions?.value || [];
  }

  public createLocationDecisionsControl(): void {
    this.addControl('locationDecisions', new FormControl([], {
      nonNullable: true,
      validators: [Validators.minLength(1), Validators.maxLength(255)]
    }));
  }

  public get surfaceType(): FormControl | null {
    return this.get('surfaceType') as FormControl | null;
  }

  public createSurfaceTypeControl(): void {
    this.addControl('surfaceType', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get description(): FormControl | null {
    return this.get('description') as FormControl | null;
  }

  public createDescriptionControl(): void {
    this.addControl('description', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public taskNameChange(v: TaskNameType): void {
    if (v === 'Inne') {
      this.createDescriptionControl();
    } else {
      this.removeControl('description');
    }
  }

  public get opinionOnTheMatterDesc(): FormControl | null {
    return this.get('opinionOnTheMatterDesc') as FormControl | null;
  }

  public createOpinionOnTheMatterDescControl(): void {
    this.addControl('opinionOnTheMatterDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get objectNameOrDevice(): FormControl | null {
    return this.get('objectNameOrDevice') as FormControl | null;
  }

  public createObjectNameOrDeviceControl(): void {
    this.addControl('objectNameOrDevice', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get objectNameOrAdvertisementDesc(): FormControl | null {
    return this.get('objectNameOrAdvertisementDesc') as FormControl | null;
  }

  public createObjectOrAdvertisementDescControl(): void {
    this.addControl('objectNameOrAdvertisementDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get taskName(): FormControl | null {
    return this.get('taskName') as FormControl | null;
  }

  public createTaskNameControl(): void {
    this.addControl('taskName', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get distance(): FormControl | null {
    return this.get('distance') as FormControl | null;
  }

  public createDistanceControl(): void {
    this.addControl('distance', new FormControl('', [Validators.required, Validators.maxLength(255)]));
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return this.parcels.value;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    const value = this.value;
    delete value.parcels;
    delete value?.locationDecisions;
    return {
      ...value,
      roadName: this.roadName.value,
      roadNumber: this.roadNumber.value,
      street: this.street?.value,

      length: toNumber(this.length?.value),
      width: toNumber(this.width?.value),
      area: toNumber(this.area?.value),
      distance: toNumber(this.distance?.value),
      description:
        this.description?.value
        || this.opinionOnTheMatterDesc?.value
        || this.objectNameOrDevice?.value
        || this.objectNameOrAdvertisementDesc?.value
        || null,
      geometry: this.geometry?.value
    };
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead): void {
    super.patchValue({
      ...value
    });
    this.taskNameChange(value.taskName || '');
    this.description?.setValue(value.description);
    this.opinionOnTheMatterDesc?.setValue(value.description);
    this.objectNameOrDevice?.setValue(value.description);
    this.objectNameOrAdvertisementDesc?.setValue(value.description);
  }

}
